import Vue from 'vue';
import { chatPersona } from '@/utils/guiareAccess';

/**
 * Estado global do assistente guIAre.
 * Vive enquanto a aba está aberta; sobrevive a trocas de rota.
 *
 *  Estados de drawer:
 *    isOpen=false  isMinimized=false  → fechado de vez
 *    isOpen=true   isMinimized=false  → drawer aberto exibindo conversa
 *    isOpen=true   isMinimized=true   → recolhido em pílula vertical
 *                                       à direita (clica pra expandir)
 *
 * SUGGESTIONS_BY_MODULE é o catálogo de perguntas de boas-vindas agrupado
 * por módulo. Na hora de exibir, filtramos só os módulos que a construtora
 * TEM dados (vem do endpoint /construtora/chat/context) e embaralhamos pra
 * mostrar combinações diferentes a cada abertura.
 */
const SUGGESTIONS_BY_MODULE = {
	chamados: [
		'Quantos chamados temos abertos agora?',
		'Quais empreendimentos têm mais chamados esse ano?',
		'Qual o tempo médio até finalização dos chamados?',
		'Quantos chamados estão parados há mais de 30 dias?',
		'Quais as categorias mais usadas na abertura de chamados?',
		'Top causas raízes nas assistências técnicas',
		'Quanto a construtora gastou com assistências esse ano?',
	],
	avaliacoes: [
		'Qual a nota média das avaliações dos clientes?',
		'Qual empreendimento tem a melhor avaliação?',
	],
	manuais: [
		'Quantos manuais temos cadastrados?',
		'Qual empreendimento tem mais manuais?',
		'Quantos mini-manuais ainda estão em revisão?',
	],
	downloads: [
		'Qual manual foi mais baixado este ano?',
		'Quantos downloads tivemos no mês passado?',
	],
	manutencao: [
		'Quantos planos de manutenção temos ativos?',
		'Quantas tarefas estão atrasadas?',
		'Esse mês as manutenções estão sendo realizadas?',
	],
	databook: [
		'Quantos databooks temos cadastrados?',
		'Qual databook tem mais GB armazenados?',
	],
	fornecedores: [
		'Quantos fornecedores temos cadastrados?',
		'Liste os fornecedores ativos',
	],
	empreendimentos: [
		'Quantos empreendimentos eu tenho?',
		'Qual o maior empreendimento da construtora?',
	],
	usuarios: [
		'Quantos usuários cadastrados temos?',
		'Quantos síndicos estão ativos?',
	],
};

const DEFAULT_FALLBACK = [
	'Quantos empreendimentos eu tenho?',
	'Liste os empreendimentos da construtora',
];

/**
 * Catálogo de sugestões do CONDÔMINO. Os módulos vêm do endpoint
 * /condomino/chat/context (chamados, documentos), escopados ao próprio usuário.
 */
const SUGGESTIONS_BY_MODULE_CONDOMINO = {
	chamados: [
		'Quais são os meus chamados?',
		'Tenho algum chamado em aberto?',
		'Qual o status do meu último chamado?',
	],
	documentos: [
		'Resuma o que preciso saber do meu imóvel',
		'Qual a cor da tinta das paredes?',
		'Até quando tenho garantia do piso?',
		'Quais documentos eu tenho disponíveis?',
		'Quais manuais foram emprestados pra mim?',
		'Tenho a planta do meu imóvel?',
	],
};

/**
 * Catálogo do SÍNDICO. Os módulos vêm de /sindico/chat/context — hoje só
 * `manutencao`, que é o que as tools da persona respondem
 * (consultar_alertas_manutencao e consultar_plano_manutencao).
 */
const SUGGESTIONS_BY_MODULE_SINDICO = {
	manutencao: [
		'O que eu preciso fazer esse mês?',
		'Tem alguma manutenção atrasada?',
		'Algum comprovante vencendo?',
		'Algum contrato de fornecedor perto de vencer?',
		'Como está a eficiência do meu plano?',
		'Quantas atividades já foram realizadas?',
	],
};

const DEFAULT_FALLBACK_SINDICO = [
	'O que eu preciso fazer esse mês?',
	'Como está a eficiência do meu plano?',
];

const DEFAULT_FALLBACK_CONDOMINO = [
	'Quais são os meus chamados?',
	'Quais documentos eu tenho disponíveis?',
];

/** Chave de persistência da conversa ativa (reidratação após reload). */
export const CONVERSATION_KEY = 'guiare_conversation_uuid';

/**
 * Monta a lista final de sugestões:
 *  - Considera só módulos com `tem_dados=true`
 *  - Pega no máximo N (default 6) distribuindo entre módulos
 *  - Embaralha pra variar a cada abertura do chat
 */
export function buildSuggestions(modulos = {}, max = 6) {
	// Três personas, não duas: sem isto o síndico caía no catálogo da
	// construtora e recebia sugestões que as tools dele não respondem.
	const persona = chatPersona();

	const catalogos = {
		condomino: SUGGESTIONS_BY_MODULE_CONDOMINO,
		sindico: SUGGESTIONS_BY_MODULE_SINDICO,
		construtora: SUGGESTIONS_BY_MODULE,
	};
	const fallbacks = {
		condomino: DEFAULT_FALLBACK_CONDOMINO,
		sindico: DEFAULT_FALLBACK_SINDICO,
		construtora: DEFAULT_FALLBACK,
	};

	const catalogo = catalogos[persona] || SUGGESTIONS_BY_MODULE;
	const fallback = fallbacks[persona] || DEFAULT_FALLBACK;

	const disponiveis = Object.entries(catalogo)
		.filter(([modulo]) => modulos[modulo])
		.flatMap(([, perguntas]) => perguntas);

	if (!disponiveis.length) return fallback;

	// Fisher-Yates shuffle
	const arr = [...disponiveis];
	for (let i = arr.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[arr[i], arr[j]] = [arr[j], arr[i]];
	}

	return arr.slice(0, max);
}

export const chatStore = Vue.observable({
	isOpen: false,
	isMinimized: false,
	conversationUuid: null,
	conversationTitle: null,
	messages: [],
	isStreaming: false,
	currentToolName: null,
	error: null,
	suggestions: DEFAULT_FALLBACK,
	contextLoaded: false,
});

export const chatActions = {
	open() {
		chatStore.isOpen = true;
		chatStore.isMinimized = false;
	},

	setSuggestionsFromModulos(modulos) {
		chatStore.suggestions = buildSuggestions(modulos);
		chatStore.contextLoaded = true;
	},

	minimize() {
		chatStore.isMinimized = true;
	},

	restore() {
		chatStore.isMinimized = false;
	},

	close() {
		chatStore.isOpen = false;
		chatStore.isMinimized = false;
	},

	setConversation(conversation) {
		chatStore.conversationUuid = conversation?.uuid || null;
		chatStore.conversationTitle = conversation?.title || null;

		// Persiste a conversa ativa pra reidratar após reload (F5).
		if (conversation?.uuid) {
			localStorage.setItem(CONVERSATION_KEY, conversation.uuid);
		} else {
			localStorage.removeItem(CONVERSATION_KEY);
		}
	},

	setMessages(messages) {
		chatStore.messages = messages || [];
	},

	pushUserMessage(content) {
		chatStore.messages.push({
			id: `local-${Date.now()}`,
			role: 'user',
			content,
			pending: false,
		});
	},

	/**
	 * Inicia uma resposta nova do assistente, em modo "streaming".
	 * Retorna o índice da mensagem na lista, pra ir concatenando os deltas.
	 */
	startAssistantMessage() {
		chatStore.messages.push({
			id: `streaming-${Date.now()}`,
			role: 'assistant',
			content: '',
			pending: true,
		});
		chatStore.isStreaming = true;
		chatStore.currentToolName = null;
		chatStore.error = null;
		return chatStore.messages.length - 1;
	},

	appendAssistantDelta(index, delta) {
		const msg = chatStore.messages[index];
		if (msg) {
			Vue.set(msg, 'content', (msg.content || '') + delta);
		}
	},

	setToolRunning(name) {
		chatStore.currentToolName = name;
	},

	/**
	 * Marca a mensagem do assistente para exibir a oferta de falar com o
	 * suporte humano — usado quando a IA registra que não conseguiu responder
	 * (tool `registrar_pergunta_nao_atendida`). O cliente decide se aciona.
	 */
	markSupportOffer(index) {
		const msg = chatStore.messages[index];
		if (msg) {
			Vue.set(msg, 'offerSupport', true);
		}
	},

	clearToolRunning() {
		chatStore.currentToolName = null;
	},

	finishAssistantMessage(index, payload = {}) {
		const msg = chatStore.messages[index];
		if (msg) {
			Vue.set(msg, 'pending', false);
			if (payload.messageId) Vue.set(msg, 'id', payload.messageId);
		}
		chatStore.isStreaming = false;
		chatStore.currentToolName = null;
	},

	setError(message) {
		chatStore.error = message;
		chatStore.isStreaming = false;
		chatStore.currentToolName = null;
	},

	reset() {
		chatStore.conversationUuid = null;
		chatStore.conversationTitle = null;
		chatStore.messages = [];
		chatStore.isStreaming = false;
		chatStore.currentToolName = null;
		chatStore.error = null;
		localStorage.removeItem(CONVERSATION_KEY);
	},

	/**
	 * Limpa a conversa atual deixando o drawer aberto e pronto pra uma nova.
	 * A próxima mensagem enviada vai criar uma conversa nova no backend.
	 */
	newConversation() {
		chatStore.conversationUuid = null;
		chatStore.conversationTitle = null;
		chatStore.messages = [];
		chatStore.isStreaming = false;
		chatStore.currentToolName = null;
		chatStore.error = null;
		localStorage.removeItem(CONVERSATION_KEY);
	},
};

export default chatStore;
